import React from "react";
import Typography from "../../../atoms/typography";
import ButtonComponent from "../../../atoms/button";
import PropTypes from "prop-types";
import {
  ADD_ON,
  ADD_SYMBOL,
  BASE_ITEM,
  EQUAL_SYMBOL,
  FREE,
  PRICE,
  DETAILS,
  S,
  SHIPPING,
  TOTAL,
} from "../../../../constants/common/common";
import { useSelector } from "react-redux";
import { updateCurrency } from "../../../../utils/common";
import dynamic from "next/dynamic";

const PriceBreakUp = dynamic(() => import("./priceBreakUp"), {
  ssr: false,
});

/**
 * Component that renders the footer for the desktop layout, displaying
 * pricing information and handling the continuation of the process.
 *
 * @param {Object} props - The component props.
 * @param {function} props.calculateAddonCount - Function to calculate the count of add-ons.
 * @param {number} props.basePrice - The base price of the product.
 * @param {function} props.calculateAddonTotal - Function to calculate the total cost of add-ons.
 * @param {function} props.calculateTotal - Function to calculate the overall total price.
 * @param {function} props.onContinueClick - Function to handle the continuation action.
 */
const DesktopFooter = ({ calculateAddonCount, basePrice, calculateAddonTotal, calculateTotal, onContinueClick }) => {
  const { timeSlots } = useSelector((state) => state.shippingDateAndSlotSelection?.selectedDeliveryDetails);
  const selectedCurrency = useSelector((state) => state?.currencyList?.userSelectedCurrency);

  const cartTotalQuantity = useSelector((state) => state.cart.cartCount || 0);

  return (
    <div className="bg-white flex items-center justify-end gap-28 border-t-1 border-lightGrey px-20">
      <div className="flex items-center space-x-2">
        <div className="flex flex-col items-start p-2.5">
          <Typography variant="body2" Tag="p" classes="font-600 text-black-100">
            {PRICE?.toLocaleUpperCase()}
          </Typography>
          <Typography variant="body2" Tag="p" classes="font-600 text-black-100">
            {DETAILS.toLocaleUpperCase()}
          </Typography>

          <PriceBreakUp />
        </div>
        <div className="flex flex-col items-start p-2.5">
          <Typography variant="body2" Tag="p" classes="font-600 text-grey-400 text-14">
            {`${cartTotalQuantity} ${BASE_ITEM}`}
          </Typography>
          <Typography variant="body2" Tag="p" classes="font-600 text-black-100">
            {updateCurrency(selectedCurrency, basePrice)}
          </Typography>
        </div>
        <div className="flex items-center p-2.5">
          <Typography variant="body2" Tag="p" className="align-middle text-14 text-gray-400">
            {ADD_SYMBOL}
          </Typography>
        </div>
        <div className="flex flex-col items-start p-2.5">
          <Typography variant="body2" Tag="p" classes="font-600 text-grey-400">
            {`${calculateAddonCount()} ${ADD_ON}`}
            {calculateAddonCount() > 1 ? S : ""}
          </Typography>
          <Typography variant="body2" Tag="p" classes="font-600 text-black-100">
            {updateCurrency(selectedCurrency, calculateAddonTotal())}
          </Typography>
        </div>
        <div className="flex items-center p-2.5">
          <Typography variant="body2" Tag="p" className="align-middle text-14 text-grey-400">
            {ADD_SYMBOL}
          </Typography>
        </div>
        <div className="flex flex-col items-start p-2.5">
          <Typography variant="body2" Tag="p" classes="font-600 text-grey-400 text-14">
            {SHIPPING}
          </Typography>
          <Typography variant="body2" Tag="p" classes="font-600 text-black-100">
            {timeSlots?.timeSlots?.shippingPrice > 0
              ? updateCurrency(selectedCurrency, timeSlots?.timeSlots?.shippingPrice)
              : FREE}
          </Typography>
        </div>
        <div className="flex items-center p-2.5">
          <Typography variant="body2" Tag="p" className="align-middle text-gray-600">
            {EQUAL_SYMBOL}
          </Typography>
        </div>
        <div className="flex flex-col items-start p-2.5 text-left">
          <Typography variant="body2" Tag="p" classes="font-600 text-grey-400 text-14">
            {TOTAL?.toLocaleUpperCase()}
          </Typography>
          <Typography variant="h5" Tag="p" classes="font-600 text-18 text-black-100">
            {updateCurrency(selectedCurrency, calculateTotal())}
          </Typography>
        </div>
      </div>

      <div className="flex h-[40px] w-[311px] items-center justify-center rounded-lg bg-[#737530] p-2.5 px-3 duration-300 ease-in-out hover:bg-opacity-80">
        <ButtonComponent
          onClick={onContinueClick}
          size="textBtn"
          buttonVariant="filled"
          label="Continue"
          classes="bg-opacity-0"
        />
      </div>
    </div>
  );
};

DesktopFooter.propTypes = {
  calculateAddonCount: PropTypes.func.isRequired,
  basePrice: PropTypes.number.isRequired,
  calculateAddonTotal: PropTypes.func.isRequired,
  calculateTotal: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
};

export default DesktopFooter;
